import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ImageService } from 'src/app/Core/services/image.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToasterService } from 'src/app/Core/services/toaster.service';

@Component({
  selector: 'app-update-image',
  templateUrl: './update-image.component.html',
  styleUrls: ['./update-image.component.css']
})
export class UpdateImageComponent implements OnInit {
  updateForm: FormGroup;
  selectedImage: any;
  minDate: Date = new Date();
  errorMessage: string;
  redirectLinkError: string;
  dateError: string;
  updatedFields: Set<string> = new Set();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private imageService: ImageService,
    private fb: FormBuilder,
    private toaster: ToasterService
  ) {
    this.updateForm = this.fb.group({
      title: ['', Validators.required],
      description: ['', Validators.required],
      start_date: ['', Validators.required],
      end_date: ['', Validators.required],
      language: ['', Validators.required],
      button_text: ['', Validators.required],
      read_more_link: ['', Validators.required],
      image_type: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.selectedImage = history.state.selectedImage;
    if (this.selectedImage) {
      this.selectedImage.start_date = this.formatForInput(this.selectedImage.start_date);
      this.selectedImage.end_date = this.formatForInput(this.selectedImage.end_date);
      this.updateForm.patchValue(this.selectedImage);
    }
  }

  private formatForInput(dateString: string): string {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    const hours = ('0' + date.getHours()).slice(-2);
    const minutes = ('0' + date.getMinutes()).slice(-2);
  
    // Return the date in 'yyyy-MM-ddThh:mm' format
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  handleInputChange(fieldName: string, newValue: any) {
    this.selectedImage[fieldName] = newValue;
    this.updatedFields.add(fieldName);
  }
  
  cancel() {
    this.router.navigate(['/upload']);
  }

  private isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  resetErrors() {
    this.errorMessage = '';
    this.dateError = '';
    this.redirectLinkError = '';
  }

  private validateFields() {
    if (this.selectedImage.image_type === 'banner') {
      if (!this.selectedImage.start_date || !this.selectedImage.end_date || !this.selectedImage.read_more_link) {
        this.errorMessage = 'Start date, end date, and redirect link are mandatory for banner images';
        return;
      }
    } else if (this.selectedImage.image_type === 'explorer') {
      if (!this.selectedImage.title || !this.selectedImage.description || !this.selectedImage.language || !this.selectedImage.button_text || !this.selectedImage.read_more_link || !this.selectedImage.start_date || !this.selectedImage.end_date) {
        this.errorMessage = 'All fields are mandatory for explorer images';
        return;
      }
    }
    const today = new Date();
    if (this.updatedFields.has('start_date') || this.updatedFields.has('end_date')) {
      const enteredStart = new Date(this.selectedImage.start_date);
      const enteredEnd = new Date(this.selectedImage.end_date);

      if (enteredStart < today) {
        this.dateError = 'Start date cannot be in the past.';
        return false;
      } else if (enteredEnd < enteredStart) {
        this.dateError = 'End date cannot be before start date.';
        return false;
      }
    }

    if (this.updatedFields.has('read_more_link') && !this.isValidUrl(this.selectedImage.read_more_link)) {
      this.redirectLinkError = 'Please enter a valid URL.';
      return false;
    }

    return true;
  }

  onUpdate() {
    this.resetErrors();

    if (!this.validateFields()) {
      return;
    }

    const updatedData: any = {};
    this.updatedFields.forEach(field => {
      updatedData[field] = this.selectedImage[field];
    });

    if (updatedData.start_date) {
      updatedData.start_date = this.formatForBackend(updatedData.start_date);
    }
    if (updatedData.end_date) {
      updatedData.end_date = this.formatForBackend(updatedData.end_date);
    }
    if (Object.keys(updatedData).length === 0) {
      this.errorMessage = 'No fields have been changed';
      return;
    }
  
    const id = this.selectedImage.uuid;
    this.imageService.updateImage(id, updatedData).subscribe(
      response => {
        this.toaster.openDialogBox('Update successful',false);
        this.router.navigate(['/upload']);
      },
      error => {
        console.error('Failed to update image', error);
        this.errorMessage = 'Update failed';
        if(error.status === 403){
        this.toaster.openDialogBox("You don't have the required permission",true);
        }
      }
    );
  }

  private formatForBackend(dateString: string): string {
    const date = new Date(dateString);
    return date.toISOString();
  }
}
